import { roundToPlaces } from "../../utils/roundToPlaces.js";
import type { Coupon } from "../Coupon.js";
import { CouponAccountValidity } from "../CouponAccountValidity.js";
import { CustomerType } from "../CustomerType.js";
import type { OrderInput } from "../Order.js";
import type { Subcategory } from "../Subcategory.js";
import type { TaxedPrice } from "../TaxedPrice.js";
import { calculatePriceWithTax } from "./calculatePriceWithTax.js";

export function calculateOrderDiscountsFromCoupons(
	order: Pick<OrderInput, "products" | "deliveryAddress" | "couponCode">,
	customerType: CustomerType,
	subcategories: Pick<Subcategory, "id" | "urlSlug">[],
	coupons: Coupon[],
): TaxedPrice[] {
	const couponCode = order.couponCode?.trim().toLowerCase();
	if (!couponCode) {
		return [];
	}

	const coupon = coupons.find(({ code }) => code.toLowerCase() === couponCode);
	if (!coupon) {
		return [];
	}
	if (coupon.accountValidity === CouponAccountValidity.ForB2b && customerType === CustomerType.B2c) {
		return [];
	}
	if (coupon.accountValidity === CouponAccountValidity.ForB2c && customerType !== CustomerType.B2c) {
		return [];
	}

	const couponEligibleSubcategoryIds = new Set(
		subcategories
			.filter(({ urlSlug }) =>
				coupon.subcategorySlugs.some((s) => s.trim().toLowerCase() === urlSlug.cs.trim().toLowerCase()),
			)
			.map((s) => s.id),
	);

	if (couponEligibleSubcategoryIds.size === 0) {
		return [];
	}

	const byTaxRate = new Map<number, number>();
	const discounts: TaxedPrice[] = [];

	for (const product of order.products) {
		if (!couponEligibleSubcategoryIds.has(product.info.subcategoryId)) {
			continue;
		}

		const priceWithoutTax = byTaxRate.get(product.info.price.taxRate) ?? 0;
		byTaxRate.set(product.info.price.taxRate, priceWithoutTax + product.info.price.withoutTax * product.quantity);
	}

	for (const [taxRate, price] of byTaxRate) {
		const discountedPriceWithoutTax = roundToPlaces(price * (coupon.discountPercentage / 100), 2);
		discounts.push({
			taxRate,
			withoutTax: discountedPriceWithoutTax,
			withTax: calculatePriceWithTax(discountedPriceWithoutTax, taxRate),
		});
	}

	return discounts;
}
